/**
 * Deterministic assembly for ICH M11 Section 7.1.3 – Rechallenge.
 *
 * General rechallenge after temporary discontinuation is authored from
 * protocol source excerpts + user responses. Liver-specific procedures
 * stay in 7.1.3.1 (cross-reference only).
 */

export const RECHALLENGE_AFTER_TEMP_DISC = {
  YES: 'yes',
  NO: 'no',
};

export const LIVER_RESTART_AFTER_STOPPING = {
  NOT_PERMITTED: 'not_permitted',
  PERMITTED: 'permitted',
};

export const RECHALLENGE_REQUIREMENT_OPTIONS = [
  { id: 'sponsor_medical_monitor', label: 'Sponsor Medical Monitor approval' },
  { id: 'sponsor_safety_review_committee', label: 'Sponsor Safety Review Committee approval' },
  { id: 'idmc', label: 'Independent Data Monitoring Committee approval' },
  { id: 'ethics_committee', label: 'Ethics Committee approval' },
  { id: 'irb', label: 'Institutional Review Board (IRB) approval' },
  { id: 'participant_reconsent', label: 'Participant re-consent' },
  { id: 'additional_liver_monitoring', label: 'Additional liver monitoring' },
  { id: 'other', label: 'Other (Specify)' },
];

export const RECHALLENGE_NOT_PERMITTED_STATEMENT =
  'Trial intervention restart or rechallenge following temporary discontinuation is not permitted in this trial.';

export const DEFAULT_RECHALLENGE_DECISIONS = {
  rechallenge_after_temporary_discontinuation: '',
  liver_restart_after_stopping: '',
  protocol_requirements: [],
  other_requirement_text: '',
  protocol_source_excerpts: '',
};

function normalizeDecisions(raw) {
  const base = {
    ...DEFAULT_RECHALLENGE_DECISIONS,
    ...(raw && typeof raw === 'object' ? raw : {}),
  };
  base.protocol_requirements = Array.isArray(base.protocol_requirements)
    ? base.protocol_requirements.filter(Boolean)
    : [];
  return base;
}

function selectedRequirementLabels(decisions) {
  const ids = new Set(decisions.protocol_requirements || []);
  const labels = RECHALLENGE_REQUIREMENT_OPTIONS.filter((o) => ids.has(o.id)).map((o) => o.label);
  if (ids.has('other')) {
    const other = String(decisions.other_requirement_text || '').trim();
    if (other) {
      const idx = labels.findIndex((l) => l.startsWith('Other'));
      if (idx >= 0) labels[idx] = `Other: ${other}`;
    }
  }
  return labels;
}

export function assembleRechallengeSection713(intake) {
  const decisions = normalizeDecisions(intake?.discontinuation_rules?.rechallenge_decisions);
  const existing = String(intake?.discontinuation_rules?.rechallenge || '').trim();
  const warnings = [];

  if (!decisions.rechallenge_after_temporary_discontinuation) {
    warnings.push(
      'Indicate whether the protocol permits rechallenge or restart after temporary discontinuation.'
    );
    return {
      rechallenge: existing,
      warnings,
      complete: false,
      needsGenerateFromSource: false,
    };
  }

  if (!decisions.liver_restart_after_stopping) {
    warnings.push('Indicate whether restart/rechallenge is permitted after liver stopping criteria are met.');
  }

  if (decisions.rechallenge_after_temporary_discontinuation === RECHALLENGE_AFTER_TEMP_DISC.NO) {
    return {
      rechallenge: RECHALLENGE_NOT_PERMITTED_STATEMENT,
      warnings,
      complete: Boolean(decisions.liver_restart_after_stopping),
      needsGenerateFromSource: false,
    };
  }

  // Yes — protocol source required for authoring
  const excerpts = String(decisions.protocol_source_excerpts || '').trim();
  if (!excerpts) {
    warnings.push(
      'Paste protocol source text that defines general rechallenge / restart after temporary discontinuation.'
    );
  }

  if (
    decisions.liver_restart_after_stopping === LIVER_RESTART_AFTER_STOPPING.PERMITTED &&
    (!decisions.protocol_requirements || decisions.protocol_requirements.length === 0)
  ) {
    warnings.push(
      'Liver restart is permitted — select all documented protocol-specific requirements (or confirm via generation from source only).'
    );
  }

  if (
    decisions.protocol_requirements?.includes('other') &&
    !String(decisions.other_requirement_text || '').trim()
  ) {
    warnings.push('Specify the “Other” protocol-specific requirement.');
  }

  const needsGenerateFromSource = Boolean(excerpts) && !existing;
  if (needsGenerateFromSource) {
    warnings.push(
      'Protocol excerpts are ready. Click “Generate 7.1.3 from Protocol Source” to author the section (no invented procedures).'
    );
  }

  const complete =
    Boolean(excerpts && existing && decisions.liver_restart_after_stopping) &&
    !(
      decisions.protocol_requirements?.includes('other') &&
      !String(decisions.other_requirement_text || '').trim()
    );

  return {
    rechallenge: existing,
    warnings,
    complete,
    needsGenerateFromSource,
    selectedRequirementLabels: selectedRequirementLabels(decisions),
  };
}

export function buildRechallenge713Prompt({ protocolExcerpts, decisions }) {
  const normalized = normalizeDecisions(decisions);
  const reqLabels = selectedRequirementLabels(normalized);
  const liverLine =
    normalized.liver_restart_after_stopping === LIVER_RESTART_AFTER_STOPPING.NOT_PERMITTED
      ? 'Restart/Rechallenge after liver stopping criteria is NOT permitted. Include a brief statement that liver-specific restart/rechallenge is not permitted and refer readers to Section 7.1.3.1. Do NOT write detailed liver chemistry procedures.'
      : normalized.liver_restart_after_stopping === LIVER_RESTART_AFTER_STOPPING.PERMITTED
        ? `Restart/Rechallenge after liver stopping criteria is permitted. Incorporate ONLY these user-selected, protocol-documented requirements if supported by the excerpts: ${
            reqLabels.length ? reqLabels.join('; ') : '(none selected — do not invent approvals)'
          }. Cross-reference Section 7.1.3.1 for liver-specific procedures; do NOT duplicate detailed 7.1.3.1 text.`
        : 'Liver restart after stopping was not answered — mention Section 7.1.3.1 only if supported by excerpts; do not invent liver criteria.';

  return `OBJECTIVE
Generate ICH M11-compliant Section 7.1.3 – Rechallenge based exclusively on protocol-defined information and the user's responses.

This section shall describe the protocol-defined circumstances under which trial intervention may be restarted or participants may be rechallenged following temporary discontinuation of trial intervention.

This section shall NOT include liver-specific restart/rechallenge procedures described in Section 7.1.3.1, except for a brief cross-reference where appropriate.

CORE AUTHORING RULES (MANDATORY)
- NEVER invent rechallenge procedures.
- NEVER infer restart criteria.
- NEVER create sponsor approval requirements not in the source.
- NEVER create safety monitoring procedures not in the source.
- NEVER generate liver chemistry criteria or laboratory thresholds.
- NEVER create dose modification rules.
- NEVER infer investigator responsibilities.
- NEVER create protocol requirements that are not explicitly documented.
- ONLY use the PROTOCOL SOURCE EXCERPTS together with the USER RESPONSES below.

USER RESPONSES
- Rechallenge/restart after temporary discontinuation: YES
- ${liverLine}

AUTHORING INSTRUCTIONS
Generate Section 7.1.3 as a single cohesive narrative paragraph (continuous protocol text, no bullet points) using only documented protocol information regarding:
- whether trial intervention may be restarted or participants may be rechallenged following temporary discontinuation;
- the general circumstances in which restart or rechallenge is permitted;
- any documented clinical or safety conditions that must be met before restart;
- any investigator, sponsor, or committee review requirements documented in the protocol;
- any participant consent or re-consent requirements documented in the protocol;
- any monitoring or assessments required before or after restart, if documented;
- any cross-reference to Section 7.1.3.1 for liver-specific restart/rechallenge procedures, where applicable.

Example style only (replace with protocol-specific content):
Trial intervention may be restarted or participants may be rechallenged following temporary discontinuation when the protocol-defined criteria for resumption of treatment have been satisfied. Before trial intervention is restarted, all protocol-specified clinical evaluations, safety assessments, and required approvals should be completed, as applicable. Participants should continue protocol-required assessments following restart in accordance with the Schedule of Activities. Liver-specific restart or rechallenge requirements are described in Section 7.1.3.1.

PROHIBITED
- Invent restart criteria, sponsor approval processes, committee responsibilities, monitoring, laboratory criteria, liver thresholds, or dose modification procedures
- Infer requirements from standard practice
- Duplicate detailed liver-specific content reserved for Section 7.1.3.1

FINAL OUTPUT
Return ONLY the section body paragraph(s) for 7.1.3 (plain text). Do not include the heading "7.1.3". No preamble, no bullets, no editorial notes.

PROTOCOL SOURCE EXCERPTS (Priority 1 — sole clinical source of truth):
---
${protocolExcerpts}
---`;
}

export function isRechallengeDeterministicField(fieldName) {
  return fieldName === 'rechallenge';
}

/** Map 7.1.3 liver Q2 → liver patient library restart_rechallenge values */
export function toLiverRestartLibraryValue(liverRestartAfterStopping) {
  if (liverRestartAfterStopping === LIVER_RESTART_AFTER_STOPPING.NOT_PERMITTED) {
    return 'not_permitted';
  }
  if (liverRestartAfterStopping === LIVER_RESTART_AFTER_STOPPING.PERMITTED) {
    return 'permitted';
  }
  return '';
}

export function fromLiverRestartLibraryValue(libraryValue) {
  if (libraryValue === 'not_permitted') return LIVER_RESTART_AFTER_STOPPING.NOT_PERMITTED;
  if (libraryValue === 'permitted') return LIVER_RESTART_AFTER_STOPPING.PERMITTED;
  return '';
}
